import type { FC, PropsWithChildren } from 'react';
import React from 'react';

import type { UseHasAppLoadedProps } from './hooks';
import { useHasFirstPageLoaded } from './hooks';

/**
 * This component defers rendering of its children until after the app's first page load has completed. This should
 * be used in the top-level render tree (e.g. App.tsx) and wrap any component, hook, API call, etc. which is not
 * critical for the first render cycle. The child component it renders should also be wrapped in a lazy-loaded
 * split bundle to avoid network congestion during the initial page load.
 *
 * Provide `excludePrecacheLoad: true` for any component which should _not_ render during background precache app
 * loads and should wait until a real user-facing page has rendered (i.e. any feature which triggers API calls which
 * would add unnecessary load on BE services during precache loads). Providing this prop as `true` will only result
 * in a behavior change in Teams integrations.
 */
export const RenderAfterFirstPageLoad: FC<PropsWithChildren<UseHasAppLoadedProps>> = ({
  excludePrecacheLoad,
  children,
}) => {
  'use memo';

  const hasFirstPageLoaded = useHasFirstPageLoaded({ excludePrecacheLoad });

  if (!hasFirstPageLoaded) {
    return null;
  }

  return <>{children}</>;
};
